import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, Luggage, MapPin, Plus, QrCode, RefreshCw, Satellite, ShieldAlert, Sparkles, Trash2, Pencil, Users, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner } from "../components/common";
import { BAG_TYPES, BAG_STATUSES, ITEM_STATUSES, ITEM_CATEGORIES, label, statusTone, photoSrc, inp } from "../components/gear/GearBits";
import BagForm from "../components/gear/BagForm";
import ItemForm from "../components/gear/ItemForm";
import AddItemsModal from "../components/gear/AddItemsModal";
import AICheckModal from "../components/gear/AICheckModal";
import ReuseBagModal from "../components/gear/ReuseBagModal";
import LostReports from "../components/gear/LostReports";
import DocumentVault from "../components/gear/DocumentVault";
import QRTagModal from "../components/gear/QRTagModal";

const Stat = ({ value, label: l, tone = "text-zinc-900", testid }) => (
  <div className="border border-zinc-300 bg-white px-4 py-3 text-center" data-testid={testid}>
    <div className={`font-heading font-900 text-2xl ${tone}`}>{value}</div>
    <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{l}</div>
  </div>
);

export default function GearInventory() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [travelers, setTravelers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [tab, setTab] = useState("bags");
  const [modal, setModal] = useState(null); // {type, payload}
  const [tripFilter, setTripFilter] = useState("");
  const [lostPrefill, setLostPrefill] = useState(null);
  const [params, setParams] = useSearchParams();

  // Scanned a printed QR bag tag (/gear?bag={id}) -> open that bag's record
  useEffect(() => {
    const bid = params.get("bag");
    if (bid && data?.bags) {
      const bag = data.bags.find((b) => b.id === bid);
      if (bag) setModal({ type: "bag", payload: bag });
      setParams({}, { replace: true });
    }
  }, [params, data, setParams]);

  const load = useCallback(() => api.get("/gear/overview").then((r) => setData(r.data)).catch(() => setData(false)), []);
  useEffect(() => {
    if (!user) return;
    load();
    api.get("/trips/travelers").then((r) => setTravelers(r.data.items || [])).catch(() => {});
    api.get("/trips").then((r) => setTrips(r.data.items || r.data || [])).catch(() => {});
  }, [user, load]);

  const closeAndReload = () => { setModal(null); load(); };

  if (authLoading || (user && data === null)) return <Spinner full />;
  if (!user) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center">
      <Luggage className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
      <h1 className="font-heading font-900 text-3xl mb-2">Luggage & Gear Inventory</h1>
      <p className="text-muted-foreground mb-6">Track every bag and every item — what you have, where it is, who owns it, and whether it came home.</p>
      <Link to="/register" data-testid="gear-signup-cta" className="inline-block px-6 py-3 bg-primary text-white font-bold">Sign up free to start</Link>
    </div>
  );
  if (data === false) return <div className="p-20 text-center text-muted-foreground">Could not load your inventory.</div>;

  const { bags, items, stats } = data;
  const tripBags = tripFilter ? bags.filter((b) => b.trip_id === tripFilter) : bags;
  const tripItems = tripFilter ? items.filter((i) => i.trip_id === tripFilter) : items;
  const tripPacked = tripItems.filter((i) => i.status === "packed").length;
  const pct = tripItems.length ? Math.round(tripPacked * 100 / tripItems.length) : 0;

  return (
    <div data-testid="gear-page">
      <PageHeader eyebrow="My Travel Hub" title="Luggage & Gear Inventory"
        subtitle="Everything you carry — from a suitcase to a single charging cable. Know what you have, where it is, who owns it, and whether it came home." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-16 -mt-2">

        {/* Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5" data-testid="gear-stats">
          <Stat value={stats.bags} label="Bags" testid="stat-bags" />
          <Stat value={stats.items} label="Items" testid="stat-items" />
          <Stat value={stats.packed} label="Packed" tone="text-emerald-600" testid="stat-packed" />
          <Stat value={stats.not_packed} label="To pack" tone="text-amber-600" testid="stat-topack" />
          <Stat value={stats.missing} label="Missing" tone={stats.missing ? "text-red-600" : "text-zinc-400"} testid="stat-missing" />
          <Stat value={stats.high_value} label="High value" testid="stat-highvalue" />
          <Stat value={stats.trackers} label="With trackers" testid="stat-trackers" />
        </div>

        {/* Trip filter + packing progress */}
        <div className="flex flex-wrap items-center gap-3 mb-5 border border-zinc-300 bg-white p-3">
          <select value={tripFilter} onChange={(e) => setTripFilter(e.target.value)} data-testid="gear-trip-filter" className="border border-zinc-300 px-3 py-2 text-sm bg-white">
            <option value="">All bags & items</option>
            {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          <div className="flex-1 min-w-[180px]">
            <div className="flex justify-between text-xs font-mono mb-1">
              <span>Packing{tripFilter ? " for this trip" : ""}</span><span data-testid="packing-pct">{pct}% complete</span>
            </div>
            <div className="h-2.5 bg-zinc-100 border border-zinc-200">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <button onClick={() => setModal({ type: "check-depart" })} data-testid="btn-forget-check"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold border border-violet-600 text-violet-700 hover:bg-violet-50">
            <Sparkles className="w-4 h-4" /> Did you forget anything?
          </button>
          <button onClick={() => setModal({ type: "check-return" })} data-testid="btn-return-check"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold border border-blue-600 text-blue-700 hover:bg-blue-50">
            <PackageCheck className="w-4 h-4" /> Check before you leave
          </button>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setModal({ type: "bag" })} data-testid="btn-add-bag"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Add bag
          </button>
          <button onClick={() => setModal({ type: "item" })} data-testid="btn-add-item"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold border border-primary text-primary hover:bg-blue-50">
            <Plus className="w-4 h-4" /> Add item
          </button>
          <button onClick={() => setModal({ type: "bulk" })} data-testid="btn-templates"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold border border-violet-600 text-violet-700 hover:bg-violet-50">
            <Sparkles className="w-4 h-4" /> Templates & AI packing list
          </button>
        </div>

        <div className="border-b border-zinc-300 flex gap-1 overflow-x-auto" data-testid="gear-tabs">
          {[["bags", `Bags & luggage (${tripBags.length})`], ["items", `All items (${tripItems.length})`],
            ["owners", "Who owns what"], ["docs", `Documents (${stats.documents || 0}${stats.docs_expiring ? ` · ${stats.docs_expiring} expiring` : ""})`],
            ["lost", `Lost & found${stats.lost_reports_open ? ` (${stats.lost_reports_open})` : ""}`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`gear-tab-${k}`} aria-selected={tab === k}
              className={`min-h-11 px-4 py-3 text-sm font-700 -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "bags" && <BagsGrid bags={tripBags} items={items} onEdit={(b) => setModal({ type: "bag", payload: b })}
            onReuse={(b) => setModal({ type: "reuse", payload: b })} onQR={(b) => setModal({ type: "qr", payload: b })}
            onLost={(b) => { setTab("lost"); setLostPrefill({ kind: "bag", ref_id: b.id, title: b.name, brand: b.brand, color: b.color, serial_number: b.serial_number, tag_number: b.luggage_tag, photo_url: b.photo_url }); }}
            onChanged={load} />}
          {tab === "items" && <ItemsPanel items={tripItems} bags={bags} onEdit={(i) => setModal({ type: "item", payload: i })} onChanged={load} />}
          {tab === "owners" && <OwnersView bags={tripBags} items={tripItems} />}
          {tab === "docs" && <DocumentVault travelers={travelers} />}
          {tab === "lost" && <LostReports bags={bags} items={items} trips={trips} prefill={lostPrefill} onPrefillUsed={() => setLostPrefill(null)} />}
        </div>
      </div>

      {modal?.type === "bag" && <BagForm bag={modal.payload} travelers={travelers} trips={trips} onSaved={closeAndReload} onClose={() => setModal(null)} />}
      {modal?.type === "item" && <ItemForm item={modal.payload} bags={bags} travelers={travelers} trips={trips} onSaved={closeAndReload} onClose={() => setModal(null)} />}
      {modal?.type === "bulk" && <AddItemsModal travelers={travelers} trips={trips} bags={bags} onDone={closeAndReload} onClose={() => setModal(null)} />}
      {modal?.type === "reuse" && <ReuseBagModal bag={modal.payload} items={items} trips={trips} onDone={closeAndReload} onClose={() => setModal(null)} />}
      {modal?.type === "qr" && <QRTagModal bag={modal.payload} onClose={() => setModal(null)} />}
      {(modal?.type === "check-depart" || modal?.type === "check-return") &&
        <AICheckModal mode={modal.type === "check-return" ? "return" : "depart"} trips={trips} onClose={() => setModal(null)} />}
    </div>
  );
}

function BagsGrid({ bags, items, onEdit, onReuse, onQR, onLost, onChanged }) {
  const setStatus = async (b, status) => {
    await api.post(`/gear/bags/${b.id}/status`, { status });
    toast.success(`${b.name}: ${label(BAG_STATUSES, status)}`);
    onChanged();
  };
  const del = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? Its items stay in your inventory (unassigned).`)) return;
    await api.delete(`/gear/bags/${b.id}`);
    onChanged();
  };
  if (bags.length === 0) return (
    <div className="border border-dashed border-zinc-300 p-10 text-center text-muted-foreground">
      <Luggage className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
      <p className="font-bold text-zinc-700 mb-1">No bags yet</p>
      <p className="text-sm">Add each suitcase, carry-on, backpack or pouch — and photograph it. A photo makes it easy to spot at the baggage belt and to prove if it's lost.</p>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bags-grid">
      {bags.map((b) => {
        const inBag = items.filter((i) => i.bag_id === b.id);
        const packed = inBag.filter((i) => i.status === "packed").length;
        return (
          <div key={b.id} className="border border-zinc-300 bg-white" data-testid={`bag-card-${b.id}`}>
            <div className="flex gap-3 p-4">
              {b.photo_url
                ? <img src={photoSrc(b.photo_url)} alt="" className="w-16 h-16 object-cover border border-zinc-200 shrink-0" />
                : <div className="w-16 h-16 bg-zinc-50 border border-zinc-200 flex items-center justify-center text-zinc-300 shrink-0"><Briefcase className="w-7 h-7" /></div>}
              <div className="min-w-0 flex-1">
                <div className="font-heading font-900 truncate">{b.name}</div>
                <div className="text-xs text-muted-foreground">{label(BAG_TYPES, b.bag_type)}{b.color ? ` · ${b.color}` : ""}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {b.owner_name && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5"><Users className="w-3 h-3" />{b.owner_name}</span>}
                  {(b.gps_tracker || b.tracker_id) && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5"><Satellite className="w-3 h-3" />{b.gps_tracker ? "GPS" : "Tracker"}</span>}
                  {b.tsa_lock && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5">TSA lock</span>}
                </div>
              </div>
            </div>
            <div className="px-4 pb-3 flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" />
              <select value={b.status || "at_home"} onChange={(e) => setStatus(b, e.target.value)} data-testid={`bag-status-${b.id}`}
                className={`px-2 py-1 text-xs font-bold border-0 cursor-pointer ${statusTone(b.status)}`}>
                {BAG_STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span className="ml-auto">{inBag.length ? `${packed}/${inBag.length} packed` : "empty"}</span>
            </div>
            <div className="border-t border-zinc-200 px-2 py-1.5 flex justify-end gap-1">
              <button onClick={() => onQR(b)} data-testid={`bag-qr-${b.id}`} title="Printable QR tag" className="p-1.5 text-zinc-400 hover:text-emerald-600"><QrCode className="w-4 h-4" /></button>
              <button onClick={() => onReuse(b)} data-testid={`bag-reuse-${b.id}`} title="Reuse for another trip" className="p-1.5 text-zinc-400 hover:text-primary"><RefreshCw className="w-4 h-4" /></button>
              <button onClick={() => onLost(b)} data-testid={`bag-lost-${b.id}`} title="Report lost" className="p-1.5 text-zinc-400 hover:text-red-600"><ShieldAlert className="w-4 h-4" /></button>
              <button onClick={() => onEdit(b)} data-testid={`bag-edit-${b.id}`} title="Edit" className="p-1.5 text-zinc-400 hover:text-primary"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(b)} data-testid={`bag-delete-${b.id}`} title="Delete" className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ItemsPanel({ items, bags, onEdit, onChanged }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [bagF, setBagF] = useState("");
  const bagName = (id) => bags.find((b) => b.id === id)?.name || "—";
  const shown = useMemo(() => items.filter((i) =>
    (!q || (i.name || "").toLowerCase().includes(q.toLowerCase())) &&
    (!cat || i.category === cat) &&
    (!bagF || i.bag_id === bagF)), [items, q, cat, bagF]);

  const togglePacked = async (i) => {
    const status = i.status === "packed" ? "not_packed" : "packed";
    await api.post("/gear/items/status", { ids: [i.id], status });
    onChanged();
  };
  const setStatus = async (i, status) => { await api.post("/gear/items/status", { ids: [i.id], status }); onChanged(); };
  const del = async (i) => { await api.delete(`/gear/items/${i.id}`); onChanged(); };

  return (
    <div data-testid="items-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" data-testid="items-search" className={`${inp} max-w-[220px]`} />
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="items-cat-filter" className="border border-zinc-300 px-3 py-2 text-sm bg-white">
          <option value="">All categories</option>
          {ITEM_CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={bagF} onChange={(e) => setBagF(e.target.value)} data-testid="items-bag-filter" className="border border-zinc-300 px-3 py-2 text-sm bg-white">
          <option value="">All bags</option>
          {bags.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      {shown.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-muted-foreground text-sm">
          No items{q || cat || bagF ? " match your filters" : " yet — add your first item, or use a template to add many at once"}.
        </div>
      ) : (
        <div className="border border-zinc-300 bg-white divide-y divide-zinc-100">
          {shown.map((i) => (
            <div key={i.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50" data-testid={`item-row-${i.id}`}>
              <input type="checkbox" checked={i.status === "packed"} onChange={() => togglePacked(i)} data-testid={`item-packed-${i.id}`}
                title="Packed?" className="w-4 h-4 accent-emerald-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <span className={`text-sm font-medium ${i.status === "packed" ? "text-zinc-400 line-through" : ""}`}>{i.name}</span>
                {i.quantity > 1 && <span className="text-xs text-muted-foreground ml-1">×{i.quantity}</span>}
                <div className="text-[11px] text-muted-foreground">
                  {label(ITEM_CATEGORIES, i.category)}{i.owner_name ? ` · ${i.owner_name}` : ""}{i.bag_id ? ` · in ${bagName(i.bag_id)}` : ""}
                  {i.carry_on_only && <span className="text-blue-600 font-bold"> · carry-on only</span>}
                  {i.restricted && <span className="text-amber-600 font-bold"> · restricted</span>}
                  {i.high_value && <span className="text-violet-600 font-bold"> · high value</span>}
                </div>
              </div>
              <select value={i.status || "not_packed"} onChange={(e) => setStatus(i, e.target.value)} data-testid={`item-status-${i.id}`}
                className={`px-1.5 py-1 text-[11px] font-bold border-0 cursor-pointer shrink-0 ${statusTone(i.status)}`}>
                {ITEM_STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={() => onEdit(i)} data-testid={`item-edit-${i.id}`} className="p-1 text-zinc-400 hover:text-primary shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(i)} data-testid={`item-delete-${i.id}`} className="p-1 text-zinc-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnersView({ bags, items }) {
  const groups = useMemo(() => {
    const g = {};
    const add = (owner, kind, thing) => { (g[owner] = g[owner] || { bags: [], items: [] })[kind].push(thing); };
    bags.forEach((b) => add(b.owner_name || "Unassigned", "bags", b));
    items.forEach((i) => add(i.owner_name || "Unassigned", "items", i));
    return Object.entries(g).sort(([a], [b]) => (a === "Shared" ? 1 : b === "Shared" ? -1 : a.localeCompare(b)));
  }, [bags, items]);
  if (groups.length === 0) return <div className="border border-dashed border-zinc-300 p-10 text-center text-muted-foreground text-sm">Nothing in the inventory yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owners-view">
      {groups.map(([owner, g]) => (
        <div key={owner} className="border border-zinc-300 bg-white p-4" data-testid={`owner-group-${owner}`}>
          <div className="flex items-center gap-2 font-heading font-900 mb-2">
            <Users className="w-4 h-4 text-primary" /> {owner}
            <span className="ml-auto text-xs font-mono text-muted-foreground">{g.bags.length} bags · {g.items.length} items</span>
          </div>
          {g.bags.length > 0 && (
            <ul className="text-sm space-y-1 mb-2">
              {g.bags.map((b) => <li key={b.id} className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-zinc-400" />{b.name}
                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 ${statusTone(b.status)}`}>{label(BAG_STATUSES, b.status || "at_home")}</span></li>)}
            </ul>
          )}
          {g.items.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-0.5 max-h-40 overflow-y-auto">
              {g.items.map((i) => <li key={i.id}>{i.name}{i.quantity > 1 ? ` ×${i.quantity}` : ""}</li>)}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
